interface UserProfileTabsFormLike {
    getPanelsRoot(): HTMLElement;
    getTabButtons(): HTMLElement[];
    getStatTriggers(): HTMLElement[];
    getHashTab(): string;
    hasTab(tab: string): boolean;
    getPointHistoryIncludeInvalidInput(): HTMLInputElement | null;
    getProfileBody(): HTMLElement | null;
    getTabsRoot(): HTMLElement | null;
    getTabSliderViewport(): HTMLElement | null;
    closestTabButton(target: Event | EventTarget | Element | null | undefined): HTMLElement | null;
}

interface UserProfileTabsHandlerLike {
    activate(tab: string, options: { syncHash: boolean }): void;
    handlePointHistoryIncludeInvalidChange(): void;
    handleFavoritesRemoveClick(event: Event): void;
}

export class UserProfileTabsListener {
    public form: UserProfileTabsFormLike;

    public handler: UserProfileTabsHandlerLike;

    public constructor(form: UserProfileTabsFormLike, handler: UserProfileTabsHandlerLike) {
        this.form = form;
        this.handler = handler;
    }

    public register(): void {
        this.registerTabButtons();
        this.registerStatTriggers();
        this.registerTabSliderViewport();
        this.registerHashChange();
        this.registerPointHistoryIncludeInvalid();
        this.registerFavoritesRemove();
    }

    public registerTabButtons(): void {
        this.form.getTabButtons().forEach((button) => {
            if (!(button instanceof HTMLElement)) return;
            button.addEventListener("click", () => {
                const tab = button.dataset.tab || "";
                if (!tab) return;
                this.handler.activate(tab, { syncHash: true });
                this.scrollTabsToTop();
            });
        });
    }

    public registerStatTriggers(): void {
        this.form.getStatTriggers().forEach((trigger) => {
            if (!(trigger instanceof HTMLElement)) return;
            trigger.addEventListener("click", () => {
                const tab = trigger.dataset.tab || "";
                if (!tab) return;
                this.handler.activate(tab, { syncHash: true });
                this.scrollTabsToTop();
            });
        });
    }

    // Any click inside the tab slider's viewport (not just a tab button — the gaps
    // and padding of the horizontal strip count too) brings the slider up to the
    // top of the screen.
    public registerTabSliderViewport(): void {
        const viewport = this.form.getTabSliderViewport();
        if (!(viewport instanceof HTMLElement)) return;

        viewport.addEventListener("click", (event) => {
            // Tab-button clicks already scroll via their own handler; avoid a
            // redundant second call for those.
            if (this.form.closestTabButton(event.target)) return;
            this.scrollTabsToTop();
        });
    }

    // Smooth-scroll the tab slider to just below the fixed navbar — same call as
    // the write-review preview tab (offset lives in $scroll-anchor-offset).
    private scrollTabsToTop(): void {
        const anchor = this.form.getTabsRoot() || this.form.getProfileBody();
        if (!(anchor instanceof HTMLElement)) return;
        anchor.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    public registerHashChange(): void {
        if (typeof window === "undefined") return;
        window.addEventListener("hashchange", () => {
            const tab = this.form.getHashTab();
            if (!this.form.hasTab(tab)) return;
            this.handler.activate(tab, { syncHash: false });
        });
    }

    public registerPointHistoryIncludeInvalid(): void {
        const includeInvalidEl = this.form.getPointHistoryIncludeInvalidInput();
        if (!(includeInvalidEl instanceof HTMLInputElement)) return;

        includeInvalidEl.addEventListener("change", () => this.handler.handlePointHistoryIncludeInvalidChange());
    }

    public registerFavoritesRemove(): void {
        const panelsRoot = this.form.getPanelsRoot();
        if (!(panelsRoot instanceof HTMLElement)) return;

        panelsRoot.addEventListener("click", (event: Event) => {
            this.handler.handleFavoritesRemoveClick(event);
        });
    }
}
